import { Suspense, useEffect } from "react";

import { isExecutableLanguage } from "@corca/tiptap";
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from "@tiptap/react";

import { CodeExecutionResults } from "@/components/tiptap/code-block/CodeExecutionResults";
import { LineWrapper } from "@/components/tiptap/utils/line-wrapper/LineWrapper";
import { twMerge } from "@/utils/twMerge";

import { CodeBlockCodeMirrorView } from "./codemirror/CodeBlockCodeMirrorViewLazy";

import type { CodeBlockOptions } from "./CodeBlockOptions";
import type { RegisterSelectionSetter } from "@/components/tiptap/CodeBlock";
import type { CodeBlockAttrs } from "@corca/tiptap";

type CodeBlockLineWrapperProps = NodeViewProps & {
  registerSelectionSetter: RegisterSelectionSetter;
};

export function CodeBlockLineWrapper({
  extension,
  node,
  getPos,
  ...props
}: CodeBlockLineWrapperProps) {
  const { doc, renderPlace } = extension.options as CodeBlockOptions;
  const { cellId, outputTimestamp, language } = node.attrs as CodeBlockAttrs;
  const isEditor = renderPlace !== "chat-message" && renderPlace !== "chat-input";

  useEffect(() => {
    if (isEditor && isExecutableLanguage(language)) {
      return doc.trackCodeBlockMount();
    }
  }, [doc, isEditor, language]);

  const content = (
    <Suspense fallback={<CodeBlockFallback node={node} extension={extension} />}>
      <CodeBlockCodeMirrorView node={node} extension={extension} getPos={getPos} {...props} />
    </Suspense>
  );

  if (renderPlace === "chat-input") {
    return <NodeViewWrapper className="flex flex-1 mb-2.5">{content}</NodeViewWrapper>;
  }

  if (renderPlace === "chat-message") {
    return <NodeViewWrapper className="flex flex-1">{content}</NodeViewWrapper>;
  }

  return (
    // The cellId/language are surfaced on the DOM so the mobile bridge can detect
    // which code block is focused: CodeMirror is a separate editor, so focusing it
    // never moves the outer ProseMirror selection into the block — DOM focus is the
    // only reliable signal.
    <NodeViewWrapper data-cell-id={cellId} data-language={language}>
      <LineWrapper
        doc={doc}
        getPos={getPos}
        contentComponent="pre"
        contentClassName="flex-col items-stretch mb-2"
      >
        {content}
        <CodeExecutionResults
          className="mt-2"
          doc={doc}
          cellId={cellId}
          outputTimestamp={outputTimestamp}
        />
      </LineWrapper>
    </NodeViewWrapper>
  );
}

function CodeBlockFallback({ node, extension }: Pick<NodeViewProps, "node" | "extension">) {
  const { renderPlace } = extension.options as CodeBlockOptions;
  const isEditor = renderPlace !== "chat-message" && renderPlace !== "chat-input";

  return (
    <div className="flex-1 text-sm" contentEditable={false}>
      <NodeViewContent className="hidden" />
      <div
        aria-hidden
        className={twMerge(
          "m-0 py-3 px-[27px] text-sm overflow-hidden whitespace-pre rounded-8 border border-slate-200 font-geist-mono leading-[1.4] dark:border-zinc-700",
          isEditor && "pl-[47px]",
        )}
      >
        {node.textContent || "\n"}
      </div>
    </div>
  );
}
